<template>
  <div class="viq-outline">
    <h3 class="viq-outline__title">Report Outline</h3>
    <p class="viq-outline__desc">
      Review and customise the sections. Add, remove, or reorder as needed.
    </p>

    <div class="viq-outline__sections">
      <div
        v-for="(section, i) in localOutline"
        :key="i"
        class="viq-outline__section"
      >
        <div class="viq-outline__section-drag">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="4" cy="2" r="1" fill="currentColor" /><circle cx="8" cy="2" r="1" fill="currentColor" />
            <circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="8" cy="6" r="1" fill="currentColor" />
            <circle cx="4" cy="10" r="1" fill="currentColor" /><circle cx="8" cy="10" r="1" fill="currentColor" />
          </svg>
        </div>

        <div class="viq-outline__section-content">
          <el-input
            v-model="localOutline[i].title"
            size="small"
            placeholder="Section title"
          />
          <el-input
            v-if="localOutline[i].editing"
            v-model="localOutline[i].description"
            type="textarea"
            :rows="2"
            size="small"
            placeholder="Section description (optional)"
          />
        </div>

        <div class="viq-outline__section-actions">
          <button
            class="viq-outline__section-btn"
            title="Toggle description"
            @click="toggleEdit(i)"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M8.5 1.5l2 2-7 7H1.5V8.5l7-7z" stroke="currentColor" stroke-width="1" stroke-linecap="round" />
            </svg>
          </button>
          <button
            v-if="i > 0"
            class="viq-outline__section-btn"
            title="Move up"
            @click="moveUp(i)"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 2v8M3 5l3-3 3 3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
            </svg>
          </button>
          <button
            v-if="i < localOutline.length - 1"
            class="viq-outline__section-btn"
            title="Move down"
            @click="moveDown(i)"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 10V2M3 7l3 3 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
            </svg>
          </button>
          <button
            class="viq-outline__section-btn viq-outline__section-btn--delete"
            title="Remove section"
            @click="removeSection(i)"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <button class="viq-outline__add" @click="addSection">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
      Add Section
    </button>

    <div class="viq-outline__refine">
      <el-input
        v-model="refineInstructions"
        size="small"
        placeholder="Refine instructions (e.g., 'add a section on licensing requirements')"
        @keyup.enter.native="refine"
      >
        <el-button slot="append" :loading="refining" @click="refine">
          Refine
        </el-button>
      </el-input>
    </div>

    <div class="viq-outline__actions">
      <el-button @click="goBack">Back</el-button>
      <el-button
        type="primary"
        :disabled="localOutline.length === 0"
        @click="confirm"
      >
        Generate Report
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViqReportOutlineEditor",
  props: {
    outline: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localOutline: [],
      refineInstructions: "",
      refining: false,
    };
  },
  watch: {
    outline: {
      immediate: true,
      deep: true,
      handler(val) {
        this.localOutline = (val || []).map((s) => ({
          title: typeof s === "string" ? s : s.title || "",
          description: typeof s === "string" ? "" : s.description || "",
          editing: false,
        }));
      },
    },
  },
  methods: {
    addSection() {
      this.localOutline.push({ title: "", description: "", editing: true });
    },
    removeSection(i) {
      this.localOutline.splice(i, 1);
    },
    moveUp(i) {
      if (i <= 0) return;
      const item = this.localOutline.splice(i, 1)[0];
      this.localOutline.splice(i - 1, 0, item);
    },
    moveDown(i) {
      if (i >= this.localOutline.length - 1) return;
      const item = this.localOutline.splice(i, 1)[0];
      this.localOutline.splice(i + 1, 0, item);
    },
    toggleEdit(i) {
      this.$set(this.localOutline[i], "editing", !this.localOutline[i].editing);
    },
    confirm() {
      const sections = this.localOutline
        .filter((s) => s.title.trim())
        .map((s) => ({
          title: s.title.trim(),
          description: s.description?.trim() || "",
        }));
      this.$emit("confirm", sections);
    },
    async refine() {
      if (!this.refineInstructions.trim()) return;
      this.refining = true;
      try {
        await this.$emit("refine", this.refineInstructions.trim());
        this.refineInstructions = "";
      } finally {
        this.refining = false;
      }
    },
    goBack() {
      this.$store.commit("Viq/SET_REPORT_STEP", 1);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-outline {
  padding: $viq-space-lg 0;

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0 0 $viq-space-sm;
  }

  &__desc {
    font-size: $viq-font-base;
    color: $viq-text-secondary;
    margin: 0 0 $viq-space-lg;
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: $viq-space-sm;
    margin-bottom: $viq-space-md;
  }

  &__section {
    display: flex;
    align-items: flex-start;
    gap: $viq-space-sm;
    padding: $viq-space-md;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;

    &-drag {
      padding-top: $viq-space-sm;
      color: $viq-text-secondary;
      cursor: grab;
    }

    &-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: $viq-space-sm;
    }

    &-actions {
      display: flex;
      gap: 2px;
    }

    &-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border: none;
      background: none;
      color: $viq-text-secondary;
      cursor: pointer;
      border-radius: $viq-radius-sm;
      transition: background $viq-transition-fast, color $viq-transition-fast;

      &:hover {
        background: $viq-bg-secondary;
        color: $viq-text-primary;
      }

      &--delete:hover {
        background: rgba(239, 68, 68, 0.1);
        color: $viq-error;
      }
    }
  }

  &__add {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    width: 100%;
    padding: $viq-space-md;
    border: 1px dashed $viq-border;
    border-radius: $viq-radius-md;
    background: none;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;
    cursor: pointer;
    transition: border-color $viq-transition-fast, color $viq-transition-fast;
    margin-bottom: $viq-space-lg;

    &:hover {
      border-color: $viq-accent;
      color: $viq-accent;
    }
  }

  &__refine {
    margin-bottom: $viq-space-lg;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: $viq-space-sm;
  }
}
</style>
